import { CalendarDoc, DayHours, HolidayOrException, WorkingType } from 'common';
import { ConcourseDb } from 'src/global/ConcourseDb';
import { Calendar } from '../model/Calendar';

export class CalendarUtils {
  static getDefaultCalendar(): Calendar {
    return new Calendar(this.getDefaultCalendarDoc());
  }

  static getDefaultCalendarDoc(): CalendarDoc {
    const workingDay: DayHours = {
      type: WorkingType.working,
      hours: [
        {
          from: '08:00',
          to: '12:00',
        },
        {
          from: '13:00',
          to: '17:00',
        },
      ],
    };

    const exceptions: HolidayOrException[] = [];
    for (let i = 23; i < 30; i++) {
      //New Year’s Day — January 1
      exceptions.push({
        name: "New Year's Day",
        from: `20${i}-01-01`,
        to: `20${i}-01-01`,
        type: WorkingType.nonWorking,
      });
      //Juneteenth National Independence Day
      exceptions.push({
        name: 'Juneteenth',
        from: `20${i}-06-19`,
        to: `20${i}-06-19`,
        type: WorkingType.nonWorking,
      });
      //Independence Day — July 4
      exceptions.push({
        name: 'Independence Day',
        from: `20${i}-07-04`,
        to: `20${i}-07-04`,
        type: WorkingType.nonWorking,
      });
      //Veterans Day — November 11
      exceptions.push({
        name: 'Veterans Day',
        from: `20${i}-11-11`,
        to: `20${i}-11-11`,
        type: WorkingType.nonWorking,
      });
      //Christmas Day — December 25
      exceptions.push({
        name: 'Christmas Day',
        from: `20${i}-12-25`,
        to: `20${i}-12-25`,
        type: WorkingType.nonWorking,
      });
    }

    return {
      _id: 'default',
      unique_id: 1,
      name: 'Standard',
      type: 'GLOBAL',
      sunday: {
        type: WorkingType.nonWorking,
      },
      monday: { ...workingDay },
      tuesday: { ...workingDay },
      wednesday: { ...workingDay },
      thursday: { ...workingDay },
      friday: { ...workingDay },
      saturday: {
        type: WorkingType.nonWorking,
      },
      exceptions,
    };
  }

  static getCalendarsDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-calendars`);
  }

  static parseCalendarDocs(docs: CalendarDoc[]): Calendar[] {
    const calendas = docs.map(doc => new Calendar(doc));
    calendas.forEach(calendar => calendar.setParent(calendas.find(c => c.id === calendar.parentId)));
    return calendas;
  }

  static getHoursPerWeek(calendarDoc: CalendarDoc): number {
    const days = [calendarDoc.sunday, calendarDoc.monday, calendarDoc.tuesday, calendarDoc.wednesday, calendarDoc.thursday, calendarDoc.friday, calendarDoc.saturday];

    let totalHours = 0;
    days.forEach(dayData => {
      if (dayData?.type === WorkingType.working && dayData?.hours?.length > 0) {
        dayData.hours.forEach(hour => {
          // Parse time strings like "08:00" to calculate hours
          const fromParts = hour.from.split(':');
          const toParts = hour.to.split(':');
          const fromHours = Number.parseFloat(fromParts[0]) + Number.parseFloat(fromParts[1]) / 60;
          const toHours = Number.parseFloat(toParts[0]) + Number.parseFloat(toParts[1]) / 60;
          totalHours += toHours - fromHours;
        });
      }
    });

    return totalHours;
  }

  /**
   * Calculate number of working days per week for a calendar
   * @param calendarDoc - The calendar document
   * @returns Number of working days in the week (0-7)
   */
  static getDaysPerWeek(calendarDoc: CalendarDoc): number {
    const days = [calendarDoc.sunday, calendarDoc.monday, calendarDoc.tuesday, calendarDoc.wednesday, calendarDoc.thursday, calendarDoc.friday, calendarDoc.saturday];

    return days.filter(dayData => dayData?.type === WorkingType.working && dayData?.hours?.length > 0).length;
  }

  static getHoursPerDay(calendarDoc: CalendarDoc): number {
    return this.getHoursPerWeek(calendarDoc) / this.getDaysPerWeek(calendarDoc) || 8;
  }
}
